<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuPortal,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { Badge } from '@/components/ui/badge'
import { columnDisplayLabels, columnSchema } from '@/types/schedule'
import DivergentSlider from './DivergentSlider.vue'
import { useValidation } from '@/composables/useValidation'
import { useGlobalValidationResults } from '@/composables/useGlobalValidationResults'
import { useValidationResultsStore } from '@/stores/validationResultsStore'

interface SliderSelection {
  id: string
  method: string
  column?: string
  palette: string
  range: [number, number]
  label: string
  isActive: boolean
}

interface Props {
  modelValue?: SliderSelection[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: () => []
})

const emit = defineEmits<{
  'update:modelValue': [value: SliderSelection[]]
}>()

// Access validation results
const { nameStatus: legacyNameStatus } = useValidation()
const { hasValidationResult } = useGlobalValidationResults()
const validationResultsStore = useValidationResultsStore()

// Create computed validation data from store (same as AgGrid)
const nameStatus = computed(() => {
  const storeResults = validationResultsStore.getValidationResults
  
  // Convert store data to the format expected by names validation
  const exact: any[] = []
  const inexact: any[] = []
  const invalid: any[] = []
  
  storeResults.forEach((result, originalName) => {
    if (result.matchedName) {
      // We have a match - determine if it's exact or inexact based on score/similarity
      if (result.score > 0.9) {
        exact.push({
          original: result.originalName,
          matched: result.matchedName,
          source: result.source || 'unknown'
        })
      } else {
        inexact.push({
          original: result.originalName,
          matched: result.matchedName,
          source: result.source || 'unknown'
        })
      }
    } else {
      // No match found
      invalid.push({
        original: result.originalName,
        matched: null,
        source: result.source || 'unknown'
      })
    }
  })
  
  return { exact, inexact, invalid }
})

// Active selections state
const activeSelections = ref<SliderSelection[]>([...(props.modelValue || [])])

// Dropdown open state
const dropdownOpen = ref(false)

// Check if we have validation data available for names coloring
const hasValidationData = computed(() => {
  return nameStatus.value && (
    nameStatus.value.exact.length > 0 || 
    nameStatus.value.inexact.length > 0 || 
    nameStatus.value.invalid.length > 0
  )
})

// Color method configuration
const colorMethods = computed(() => ({
  names: { 
    label: 'Name',
    // Use validation palette when validation data is available, otherwise use qualitative
    palette: 'rag' as const,
    hasSubmenu: false
  },
  diversity: { 
    label: 'Diversity',
    palette: 'divergent' as const,
    hasSubmenu: false
  },
  biosecurity: { 
    label: 'Biosecurity',
    palette: 'divergent' as const,
    hasSubmenu: false
  },
  availability: { 
    label: 'Availability',
    palette: 'rag' as const,
    hasSubmenu: false
  },
  
  taxa: { 
    label: 'Taxa',
    palette: 'qualitative' as const,
    hasSubmenu: true 
  },
  column: { 
    label: 'Column',
    palette: 'divergent' as const,
    hasSubmenu: true 
  }
}))

// Taxa submenu options
const taxaOptions = {
  family: { 
    label: 'Family',
    palette: 'qualitative' as const
  }
} as const

// Available columns from schedule.ts
const availableColumns = computed(() => 
  Object.entries(columnDisplayLabels).map(([key, label]) => ({
    key,
    label,
    colormap: columnSchema.properties[key as keyof typeof columnSchema.properties]?.colormap || 'divergent'
  }))
)

// Check if a method is selected
const isMethodSelected = (methodKey: string) => {
  return activeSelections.value.some(selection => 
    selection.method === methodKey && !selection.column
  )
}

// Check if a column is selected
const isColumnSelected = (columnKey: string) => {
  return activeSelections.value.some(selection => 
    selection.method === 'column' && selection.column === columnKey
  )
}

// Check if a taxa option is selected
const isTaxaSelected = (taxaKey: string) => {
  return activeSelections.value.some(selection => 
    selection.method === taxaKey
  )
}

// Generate unique ID for selections
const generateId = () => {
  return Math.random().toString(36).substr(2, 9)
}

// Create label for selection
const createLabel = (method: string, column?: string) => {
  if (method === 'column' && column) {
    const columnConfig = availableColumns.value.find(col => col.key === column)
    return columnConfig ? columnConfig.label : column
  }
  // Check if it's a taxa option
  if (method in taxaOptions) {
    return taxaOptions[method as keyof typeof taxaOptions]?.label || method
  }
  
  // For names method, add validation info to label if available
  if (method === 'names' && hasValidationData.value) {
    return 'Names'
  }
  
  return colorMethods.value[method as keyof typeof colorMethods.value]?.label || method
}

// Get palette for method/column
const getPalette = (method: string, column?: string) => {
  if (method === 'column' && column) {
    const columnConfig = availableColumns.value.find(col => col.key === column)
    return columnConfig?.colormap || 'divergent'
  }
  // Check if it's a taxa option
  if (method in taxaOptions) {
    return taxaOptions[method as keyof typeof taxaOptions]?.palette || 'qualitative'
  }
  return colorMethods.value[method as keyof typeof colorMethods.value]?.palette || 'divergent'
}

// Toggle method selection
const toggleMethod = (methodKey: string) => {
  const existingIndex = activeSelections.value.findIndex(selection => 
    selection.method === methodKey && !selection.column
  )
  
  if (existingIndex !== -1) {
    // Remove selection
    activeSelections.value.splice(existingIndex, 1)
  } else {
    // Add new selection
    const newSelection: SliderSelection = {
      id: generateId(),
      method: methodKey,
      palette: colorMethods.value[methodKey as keyof typeof colorMethods.value]?.palette || 'divergent',
      range: [0, 1],
      label: createLabel(methodKey),
      isActive: true
    }
    activeSelections.value.push(newSelection)
  }
  
  emitUpdate()
}

// Toggle column selection
const toggleColumn = (columnKey: string) => {
  const existingIndex = activeSelections.value.findIndex(selection => 
    selection.method === 'column' && selection.column === columnKey
  )
  
  if (existingIndex !== -1) {
    // Remove selection
    activeSelections.value.splice(existingIndex, 1)
  } else {
    // Add new selection
    const newSelection: SliderSelection = {
      id: generateId(),
      method: 'column',
      column: columnKey,
      palette: getPalette('column', columnKey),
      range: [0, 1],
      label: createLabel('column', columnKey),
      isActive: true
    }
    activeSelections.value.push(newSelection)
  }
  
  emitUpdate()
}

// Toggle taxa selection
const toggleTaxa = (taxaKey: string) => {
  const existingIndex = activeSelections.value.findIndex(selection => 
    selection.method === taxaKey
  )
  
  if (existingIndex !== -1) {
    // Remove selection
    activeSelections.value.splice(existingIndex, 1)
  } else {
    // Add new selection
    const newSelection: SliderSelection = {
      id: generateId(),
      method: taxaKey,
      palette: getPalette(taxaKey),
      range: [0, 1],
      label: createLabel(taxaKey),
      isActive: true
    }
    activeSelections.value.push(newSelection)
  }
  
  emitUpdate()
}

// Remove selection by ID
const removeSelection = (id: string) => {
  const index = activeSelections.value.findIndex(selection => selection.id === id)
  if (index !== -1) {
    activeSelections.value.splice(index, 1)
    emitUpdate()
  }
}

// Update selection active state
const updateSelectionIsActive = (id: string, newIsActive: boolean) => {
  const selection = activeSelections.value.find(s => s.id === id)
  if (selection) {
    selection.isActive = newIsActive
    emitUpdate()
  }
}

// Update selection range
const updateSelectionRange = (id: string, newRange: [number, number]) => {
  const selection = activeSelections.value.find(s => s.id === id)
  if (selection) {
    selection.range = newRange
    emitUpdate()
  }
}

// Update selection palette
const updateSelectionPalette = (id: string, newPalette: string) => {
  const selection = activeSelections.value.find(s => s.id === id)
  if (selection) {
    selection.palette = newPalette
    emitUpdate()
  }
}

// Emit update to parent
const emitUpdate = () => {
  emit('update:modelValue', [...activeSelections.value])
}

// Watch for prop changes
watch(() => props.modelValue, (newValue) => {
  if (newValue) {
    activeSelections.value = [...newValue]
  }
}, { deep: true })

// Watch validation data changes and update labels for names method
watch([nameStatus, hasValidationData], () => {
  activeSelections.value.forEach(selection => {
    if (selection.method === 'names') {
      selection.label = createLabel('names')
      // Update palette to rag when validation data becomes available
      selection.palette = hasValidationData.value ? 'rag' : 'qualitative'
    }
  })
  emitUpdate()
}, { deep: true })

</script>

<template>
  <div class="w-full font-sans space-y-4 p-4">
    <!-- Dropdown for adding selections -->
    <div class="flex items-center gap-3 w-full">
      <DropdownMenu 
        v-model:open="dropdownOpen"
        :modal="false"
      >
        <DropdownMenuTrigger as-child>
          <div class="flex items-center gap-3 flex-wrap cursor-pointer group p-1 rounded-lg" style="background-color: transparent !important;">
            <!-- Show badges for active selections -->
            <template v-if="activeSelections.length > 0">
              <Badge 
                v-for="selection in activeSelections"
                :key="selection.id"
                variant="secondary"
                class="hover:bg-secondary/80 transition-colors pr-1 gap-2 group/badge m-1"
              >
                <span class="select-none">{{ selection.label }}</span>
                <button
                  @click.stop="removeSelection(selection.id)"
                  class="ml-1 aspect-square hover:bg-secondary-foreground/20 rounded-full transition-colors w-4 h-4"
                  title="Remove filter"
                >
                  <Icon name="lucide:x" class="w-3 h-3 relative -top-[1px]" />
                </button>
              </Badge>
            </template>
            
            <!-- Add button when there are selections, or default button when none -->
            <button 
              class="bg-primary/80 text-primary-foreground px-2 py-0.5 m-1 rounded-full text-xs hover:bg-primary hover:text-primary-foreground transition-colors whitespace-nowrap flex items-center gap-1.5 select-none"
              :class="{ 'border-solid bg-accent text-accent-foreground': dropdownOpen }"
            >
              <Icon name="lucide:plus" class="w-3 h-3" />
              <span v-if="activeSelections.length === 0">Color By</span>
              <span v-else>Add</span>
            </button>
          </div>
        </DropdownMenuTrigger>
        <DropdownMenuPortal>
          <DropdownMenuContent 
            :alignOffset="0" 
            align="start"
            :sideOffset="-30"
            class="w-64 rounded-xl p-2 bg-popover border border-border shadow-lg"
            :collision-padding="8"
            :avoid-collisions="true"
          >
            <!-- Regular Methods -->
            <template v-for="(method, key) in colorMethods" :key="key">
              <DropdownMenuSub v-if="method.hasSubmenu && key === 'taxa'">
                <DropdownMenuSubTrigger 
                  class="cursor-pointer rounded-lg px-3 py-2 flex items-center justify-between"
                >
                  <span>{{ method.label }}</span>
                  
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent 
                    class="w-56 rounded-xl p-2 bg-popover border border-border shadow-lg"
                    :collision-padding="8"
                    :avoid-collisions="true"
                  >
                    <DropdownMenuItem 
                      v-for="(taxa, taxaKey) in taxaOptions" 
                      :key="taxaKey"
                      @click="toggleTaxa(taxaKey)"
                      class="cursor-pointer rounded-lg px-3 py-2 flex items-center justify-between"
                    >
                      <span>{{ taxa.label }}</span>
                      <Icon 
                        v-if="isTaxaSelected(taxaKey)"
                        name="lucide:check" 
                        class="w-4 h-4 text-primary"
                      />
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuSub v-else-if="method.hasSubmenu && key === 'column'">
                <DropdownMenuSubTrigger 
                  class="cursor-pointer rounded-lg px-3 py-2 flex items-center justify-between"
                >
                  <span>{{ method.label }}</span>
                  
                </DropdownMenuSubTrigger>
                <DropdownMenuPortal>
                  <DropdownMenuSubContent 
                    class="w-56 rounded-xl p-2 bg-popover border border-border shadow-lg"
                    :collision-padding="8"
                    :avoid-collisions="true"
                  >
                    <DropdownMenuItem 
                      v-for="column in availableColumns" 
                      :key="column.key"
                      @click="toggleColumn(column.key)"
                      class="cursor-pointer rounded-lg px-3 py-2 flex items-center justify-between"
                    >
                      <span>{{ column.label }}</span>
                      <Icon 
                        v-if="isColumnSelected(column.key)"
                        name="lucide:check" 
                        class="w-4 h-4 text-primary"
                      />
                    </DropdownMenuItem>
                  </DropdownMenuSubContent>
                </DropdownMenuPortal>
              </DropdownMenuSub>
              <DropdownMenuItem 
                v-else
                @click="toggleMethod(key)"
                class="cursor-pointer rounded-lg px-3 py-2 flex items-center justify-between"
              >
                <span>{{ method.label }}</span>
                <Icon 
                  v-if="isMethodSelected(key)"
                  name="lucide:check" 
                  class="w-4 h-4 text-primary"
                />
              </DropdownMenuItem>
            </template>
          </DropdownMenuContent>
        </DropdownMenuPortal>
      </DropdownMenu>
    </div>

    <!-- Active Sliders -->
    <div v-if="activeSelections.length > 0" class="space-y-3">
      <DivergentSlider
        v-for="selection in activeSelections"
        :key="selection.id"
        :modelValue="selection.range"
        :method="selection.method"
        :column="selection.column"
        :palette="selection.palette"
        :label="selection.label"
        :removable="true"
        :is-active="selection.isActive"
        @update:modelValue="(newRange) => updateSelectionRange(selection.id, newRange)"
        @update:isActive="(newIsActive) => updateSelectionIsActive(selection.id, newIsActive)"
        @update:palette="(newPalette) => updateSelectionPalette(selection.id, newPalette)"
        @remove="removeSelection(selection.id)"
      />
    </div>

    <!-- Empty state -->
    
  </div>
</template>

<style scoped>
.data-[state=open]:bg-accent {
    background-color: hsl(var(--background)) !important;
}

[data-state="open"] {
    background-color: hsl(var(--background)) !important;
}

[role="menuitem"][data-state="open"] {
    background-color: hsl(var(--background)) !important;
}

[role="menuitem"]:hover {
    background-color: hsl(var(--background)) !important;
}

[role="menuitem"] {
    border-radius: 0.5rem !important;
}


[class*="radix-vue-dropdown-menu-trigger"] {
    background-color: hsl(var(--transparent)) !important;
    border-radius: 1rem !important;
}


</style>
